import React from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { jobsApi } from '../services/api';
import JobStatusChip from '../components/JobStatusChip';

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value ?? '—'}</Typography>
    </Box>
  );
}

function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: job, isLoading, error, refetch } = useQuery(
    ['job', id],
    () => jobsApi.get(id).then((r) => r.data),
    {
      refetchInterval: (data) =>
        data?.status === 'running' ? 3000 : false,
    }
  );

  const runMutation = useMutation(() => jobsApi.run(id), {
    onSuccess: () => queryClient.invalidateQueries(['job', id]),
  });

  if (isLoading) return <CircularProgress sx={{ m: 4 }} />;
  if (error)
    return (
      <Alert severity="error" sx={{ m: 4 }}>
        Error loading the job.
      </Alert>
    );

  const progressPct = job.progress_pct;

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/jobs')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, ml: 1 }}>
            {job.name}
          </Typography>
          <Tooltip title="Progress Dashboard">
            <IconButton color="inherit" onClick={() => navigate('/dashboard')}>
              <DashboardIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Bundle Downloads">
            <IconButton color="inherit" onClick={() => navigate('/bundle')}>
              <CloudDownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton color="inherit" onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Grid container spacing={3}>
          {/* Estado y acciones */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <JobStatusChip status={job.status} />
                <Chip label={job.job_type} variant="outlined" size="small" />
                {job.status === 'pending' || job.status === 'failed' ? (
                  <Button
                    variant="contained"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => runMutation.mutate()}
                    disabled={runMutation.isLoading}
                  >
                    {runMutation.isLoading ? 'Starting…' : 'Run'}
                  </Button>
                ) : null}
                {job.status === 'completed' && job.report_path && (
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    component="a"
                    href={jobsApi.reportUrl(id)}
                  >
                    Download report
                  </Button>
                )}
                {job.output_dir && (
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    component="a"
                    href={jobsApi.auditLogUrl(id)}
                  >
                    Audit log
                  </Button>
                )}
              </Box>

              {job.status === 'running' && (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant={progressPct != null ? 'determinate' : 'indeterminate'}
                    value={progressPct ?? 0}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {progressPct != null
                      ? `${progressPct}% (${job.processed_items ?? 0} / ${job.total_items ?? '?'} PDFs)`
                      : 'Processing…'}
                  </Typography>
                </Box>
              )}

              {runMutation.error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {runMutation.error.response?.data?.detail || 'Error launching the job'}
                </Alert>
              )}

              {job.error_message && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <strong>Error:</strong> {job.error_message}
                </Alert>
              )}
            </Paper>
          </Grid>

          {/* Detalles del job */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Configuration
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <InfoRow label="Name" value={job.name} />
              <InfoRow label="Type" value={job.job_type} />
              <InfoRow label="Modelo Gemini" value={job.model} />
              <InfoRow label="Workers" value={job.workers} />
              <InfoRow label="Force reprocess" value={job.force_reprocess ? 'Yes' : 'No'} />
              <InfoRow label="Generate report" value={job.generate_report ? 'Yes' : 'No'} />
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Paths
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <InfoRow label="ITR Log" value={job.itr_log_path} />
              <InfoRow label="PDFs (copies dir)" value={job.pdf_copies_dir || '(default)'} />
              <InfoRow label="RTA Catalogue" value={job.rta_catalogue_path || '(default)'} />
              <InfoRow label="Output directory" value={job.output_dir} />
              <InfoRow label="Report" value={job.report_path || '—'} />
            </Paper>
          </Grid>

          {/* Tiempos */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Timestamps
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <InfoRow
                label="Created"
                value={job.created_at ? new Date(job.created_at).toLocaleString() : null}
              />
              <InfoRow
                label="Started"
                value={job.started_at ? new Date(job.started_at).toLocaleString() : null}
              />
              <InfoRow
                label="Completed"
                value={job.completed_at ? new Date(job.completed_at).toLocaleString() : null}
              />
              <InfoRow label="Created by" value={job.created_by?.username} />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default JobDetailPage;
